import {
  Table as MUITable,
  type TableProps as MUITableProps,
  TableRow,
  TableBody,
  TableHead,
  TableContainer,
  Paper,
  type TableRowProps,
  useTheme
} from '@mui/material';
import { type TableColumn } from './types';
import { TableCell } from './components/TableCell';
import { HeaderCell } from './components/HeaderCell';
import { EmptyRow } from './components/EmptyRow';
import * as array from 'src/shared/utils/array';
import { type Components, TableVirtuoso } from 'react-virtuoso';
import React, { useMemo } from 'react';

export type TableProps<T> = {
  rows: T[];
  columns: Array<TableColumn<T>>;
  muiTableProps?: MUITableProps;
  virtualizedProps?: {
    height: number | string;
  };
  getRowProps?: (row: T) => TableRowProps;
};

const DefaultTable = <T,>({ rows, columns, muiTableProps, getRowProps }: TableProps<T>) => {
  return (
    <TableContainer component={Paper}>
      <MUITable sx={{ whiteSpace: 'nowrap' }} {...muiTableProps}>
        <TableHead>
          <TableRow>
            {columns.map((column, i) => (
              <HeaderCell key={i} column={column} />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!array.any(rows) ? (
            <EmptyRow colSpan={columns.length} />
          ) : (
            rows.map((row, i) => {
              const rowProps = getRowProps ? getRowProps(row) : {};
              return (
                <TableRow hover key={i} {...rowProps}>
                  {columns.map(column => (
                    <TableCell key={column.accessor} index={i} row={row} column={column} />
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </MUITable>
    </TableContainer>
  );
};

const VirtualTable = <T,>({ rows, columns, muiTableProps, virtualizedProps }: TableProps<T>) => {
  const theme = useTheme();
  const TableComponents = useMemo<Components>(() => {
    return {
      Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
        <TableContainer component={Paper} {...props} {...muiTableProps} ref={ref} />
      )),
      Table: (props: React.ComponentProps<typeof MUITable>) => (
        <MUITable {...props} style={{ borderCollapse: 'separate' }} />
      ),
      TableHead: (props: React.ComponentProps<typeof TableHead>) => (
        <TableHead {...props} style={{ background: theme.palette.background.paper, ...props.style }} />
      ),
      TableRow,
      TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => <TableBody {...props} ref={ref} />)
    };
  }, [muiTableProps, theme]);
  return (
    <TableVirtuoso
      style={{ height: virtualizedProps?.height }}
      data={rows}
      components={TableComponents}
      fixedHeaderContent={() => (
        <TableRow>
          {columns.map((column, i) => (
            <HeaderCell key={i} column={column} />
          ))}
        </TableRow>
      )}
      itemContent={(index, row: T) => (
        <>
          {columns.map(column => (
            <TableCell key={column.accessor} index={index} row={row} column={column} />
          ))}
        </>
      )}
    />
  );
};

export const Table = <T,>(props: TableProps<T>) => {
  if (props.virtualizedProps) {
    return <VirtualTable {...props} />;
  }
  return <DefaultTable {...props} />;
};
